
import React, { useState, useEffect } from 'react';
import {
    Table, Card, Button, Space, Tag, Input, Modal, Form,
    Select, Typography, message, Statistic, Row, Col,
    Popconfirm, Avatar, Tooltip, Badge, InputNumber, Alert
} from 'antd';
import {
    KeyOutlined,
    PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
    UserOutlined, CheckCircleOutlined, ClockCircleOutlined,
    WarningOutlined, LockOutlined, UnlockOutlined, UserAddOutlined,
    DollarOutlined, ProfileOutlined
} from '@ant-design/icons';
import userService from '../services/userService';
import phoneCountryCodes from '../../shared/utils/phoneCountryCodes';
import CountryFlag from '../../shared/components/CountryFlag';
import UserDetailDrawer from './UserDetailDrawer';
import { formatMoney, PLATFORM_CURRENCY } from '../../shared/utils/money';

const { Title } = Typography;
const { Option } = Select;
const { confirm } = Modal;

const UserManagement = () => {
    // 在组件内部添加activeTabKey状态
    const [activeTabKey, setActiveTabKey] = useState('active');

    const [users, setUsers] = useState([]);
    const [pendingUsers, setPendingUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [form] = Form.useForm();
    const [searchText, setSearchText] = useState('');
    const [stats, setStats] = useState({
        totalUsers: 0,
        activeUsers: 0,
        pendingUsers: 0,
        adminUsers: 0
    });

    // 余额管理相关状态
    const [balanceModalVisible, setBalanceModalVisible] = useState(false);
    const [selectedUserForBalance, setSelectedUserForBalance] = useState(null);
    const [balanceForm] = Form.useForm();

    // 用户详情抽屉状态
    const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
    const [selectedUserForDetail, setSelectedUserForDetail] = useState(null);

    const showUserDetail = (user) => {
        setSelectedUserForDetail(user);
        setDetailDrawerVisible(true);
    };

    // 获取用户列表
    const fetchUsers = async () => {
        setLoading(true);
        try {
            const result = await userService.getUsers({ search: searchText });
            if (result.success) {
                const allUsers = result.data;
                const activeUsers = allUsers.filter(user => user.status === 'active');
                const pending = allUsers.filter(user => user.status === 'pending');
                const admins = allUsers.filter(user => user.role === 'admin');

                setUsers(activeUsers);
                setPendingUsers(pending);
                setStats({
                    totalUsers: allUsers.length,
                    activeUsers: activeUsers.length,
                    pendingUsers: pending.length,
                    adminUsers: admins.length
                });
            } else {
                message.error(result.error || '获取用户列表失败');
            }
        } catch (error) {
            message.error('获取用户列表失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [searchText]);

    // 显示创建用户模态框
    const showCreateModal = () => {
        form.resetFields();
        setEditingUser(null);
        setIsModalVisible(true);
    };

    // 显示编辑用户模态框
    const showEditModal = (user) => {
        setEditingUser(user);
        form.setFieldsValue({
            username: user.username,
            email: user.email,
            role: user.role,
            status: user.status,
            phone: user.phone || '',
            phone_country_code: user.phone_country_code || '+86',
            company: user.company || ''
        });
        setIsModalVisible(true);
    };

    // 处理表单提交
    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (editingUser) {
                // 更新用户
                const result = await userService.updateUser({
                    id: editingUser.id,
                    ...values
                });

                if (result.success) {
                    message.success('用户更新成功');
                    setIsModalVisible(false);
                    fetchUsers();
                } else {
                    message.error(result.error || '更新用户失败');
                }
            } else {
                // 创建新用户
                const result = await userService.createUser(values);

                if (result.success) {
                    message.success('用户创建成功');
                    setIsModalVisible(false);
                    fetchUsers();

                    // 如果有生成的密码，显示给管理员
                    if (result.data?.password) {
                        Modal.info({
                            title: '用户创建成功',
                            content: (
                                <div>
                                    <p>请将以下信息提供给用户：</p>
                                    <p><strong>用户名:</strong> {values.username}</p>
                                    <p><strong>初始密码:</strong> {result.data.password}</p>
                                    <p>初次登录后用户需要修改密码。</p>
                                </div>
                            ),
                            okText: '我已记录'
                        });
                    }
                } else {
                    message.error(result.error || '创建用户失败');
                }
            }
        } catch (error) {
            console.log('表单验证失败:', error);
        } finally {
            setLoading(false);
        }
    };

    // 删除用户
    const deleteUser = (userId) => {
        confirm({
            title: '确认删除',
            content: '确定要删除此用户吗？此操作无法撤销。',
            okText: '删除',
            okType: 'danger',
            cancelText: '取消',
            onOk: async () => {
                try {
                    setLoading(true);
                    const result = await userService.deleteUser(userId);

                    if (result.success) {
                        message.success('用户删除成功');
                        fetchUsers();
                    } else {
                        message.error(result.error || '删除用户失败');
                    }
                } catch (error) {
                    message.error('删除用户失败: ' + error.message);
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    // 批准用户注册
    const approveUser = async (userId) => {
        try {
            setLoading(true);
            const result = await userService.updateUser({
                id: userId,
                status: 'active'
            });

            if (result.success) {
                message.success('用户已批准');
                fetchUsers();
            } else {
                message.error(result.error || '批准用户失败');
            }
        } catch (error) {
            message.error('批准用户失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // 锁定/解锁用户
    const toggleUserLock = async (user) => {
        const newStatus = user.status === 'locked' ? 'active' : 'locked';
        const actionText = user.status === 'locked' ? '解锁' : '锁定';

        try {
            setLoading(true);
            const result = await userService.updateUser({
                id: user.id,
                status: newStatus
            });

            if (result.success) {
                message.success(`用户已${actionText}`);
                fetchUsers();
            } else {
                message.error(result.error || `${actionText}用户失败`);
            }
        } catch (error) {
            message.error(`${actionText}用户失败: ` + error.message);
        } finally {
            setLoading(false);
        }
    };

    // 重置用户密码
    const resetUserPassword = async (userId) => {
        try {
            setLoading(true);
            const result = await userService.resetPassword(userId);

            if (result.success && result.data?.password) {
                Modal.success({
                    title: '密码重置成功',
                    content: (
                        <div>
                            <p>新的临时密码:</p>
                            <p><strong>{result.data.password}</strong></p>
                            <p>请将此密码提供给用户，并要求他们在首次登录后修改密码。</p>
                        </div>
                    )
                });
            } else {
                message.error(result.error || '重置密码失败');
            }
        } catch (error) {
            message.error('重置密码失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // 显示余额管理模态框
    const showBalanceModal = (user) => {
        setSelectedUserForBalance(user);
        balanceForm.resetFields();
        setBalanceModalVisible(true);
    };

    // 调整用户余额
    const handleAdjustBalance = async () => {
        try {
            const values = await balanceForm.validateFields();
            setLoading(true);

            const result = await userService.adjustBalance(
                selectedUserForBalance.id,
                parseFloat(values.amount),
                values.description
            );

            if (result.success) {
                message.success(values.amount > 0 ? '充值成功' : '扣款成功');
                setBalanceModalVisible(false);
                fetchUsers(); // 刷新用户列表
            } else {
                message.error(result.error || '操作失败');
            }
        } catch (error) {
            if (error.errorFields) {
                // 表单验证失败
                return;
            }
            message.error('操作失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // 渲染用户状态
    const renderUserStatus = (status) => {
        switch (status) {
            case 'active':
                return <Badge status="success" text="活跃" />;
            case 'pending':
                return <Badge status="processing" text="待审核" />;
            case 'locked':
                return <Badge status="error" text="已锁定" />;
            case 'inactive':
                return <Badge status="default" text="未激活" />;
            default:
                return <Badge status="default" text={status} />;
        }
    };

    // 活跃用户列表列配置
    const activeColumns = [
        {
            title: '用户',
            dataIndex: 'username',
            key: 'username',
            render: (text, record) => (
                <Space>
                    <Avatar src={record.avatar_url} icon={<UserOutlined />} />
                    <span>{text}</span>
                    {record.role === 'admin' && <Tag color="purple">管理员</Tag>}
                </Space>
            ),
        },
        {
            title: '邮箱',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: renderUserStatus,
        },
        {
            title: '余额',
            dataIndex: 'balance',
            key: 'balance',
            render: (balance, record) => (
                <Tag color="green">{formatMoney(parseFloat(balance) || 0, record.balance_currency || PLATFORM_CURRENCY)}</Tag>
            )
        },
        {
            title: '电话',
            dataIndex: 'phone',
            key: 'phone',
            render: (text, record) => text ? `${record.phone_country_code || '+86'} ${text}` : '-'
        },
        {
            title: '注册时间',
            dataIndex: 'created_at',
            key: 'created_at',
            render: text => new Date(text).toLocaleString()
        },
        {
            title: '操作',
            key: 'action',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="用户详情">
                        <Button
                            type="default"
                            size="small"
                            icon={<ProfileOutlined />}
                            onClick={() => showUserDetail(record)}
                        />
                    </Tooltip>

                    <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => showEditModal(record)}
                    >
                        编辑
                    </Button>

                    <Button
                        type={record.status === 'locked' ? 'primary' : 'default'}
                        danger={record.status !== 'locked'}
                        size="small"
                        icon={record.status === 'locked' ? <UnlockOutlined /> : <LockOutlined />}
                        onClick={() => toggleUserLock(record)}
                    >
                        {record.status === 'locked' ? '解锁' : '锁定'}
                    </Button>

                    <Tooltip title="余额管理">
                        <Button
                            type="default"
                            size="small"
                            icon={<DollarOutlined />}
                            onClick={() => showBalanceModal(record)}
                        />
                    </Tooltip>

                    <Tooltip title="重置密码">
                        <Button
                            type="default"
                            size="small"
                            icon={<KeyOutlined />}
                            onClick={() => resetUserPassword(record.id)}
                        />
                    </Tooltip>

                    <Popconfirm
                        title="确定要删除此用户吗?"
                        onConfirm={() => deleteUser(record.id)}
                        okText="是"
                        cancelText="否"
                    >
                        <Button
                            type="default"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                        >
                            删除
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // 待审核用户列表列配置
    const pendingColumns = [
        {
            title: '用户',
            dataIndex: 'username',
            key: 'username',
            render: (text, record) => (
                <Space>
                    <Avatar src={record.avatar_url} icon={<UserOutlined />} />
                    <span>{text}</span>
                </Space>
            ),
        },
        {
            title: '邮箱',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: '电话',
            dataIndex: 'phone',
            key: 'phone',
            render: (text, record) => text ? `${record.phone_country_code || '+86'} ${text}` : '-'
        },
        {
            title: '注册时间',
            dataIndex: 'created_at',
            key: 'created_at',
            render: text => new Date(text).toLocaleString()
        },
        {
            title: '操作',
            key: 'action',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="primary"
                        size="small"
                        icon={<CheckCircleOutlined />}
                        onClick={() => approveUser(record.id)}
                    >
                        批准
                    </Button>

                    <Button
                        type="default"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => showEditModal(record)}
                    >
                        编辑
                    </Button>

                    <Popconfirm
                        title="确定要删除此用户吗?"
                        onConfirm={() => deleteUser(record.id)}
                        okText="是"
                        cancelText="否"
                    >
                        <Button
                            type="default"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                        >
                            拒绝
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Title level={2}>用户管理</Title>

            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false}>
                        <Statistic
                            title="总用户数"
                            value={stats.totalUsers}
                            prefix={<UserOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false}>
                        <Statistic
                            title="活跃用户"
                            value={stats.activeUsers}
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false}>
                        <Statistic
                            title="待审核用户"
                            value={stats.pendingUsers}
                            prefix={<ClockCircleOutlined />}
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false}>
                        <Statistic
                            title="管理员"
                            value={stats.adminUsers}
                            prefix={<WarningOutlined />}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Card
                extra={
                    <Space>
                        <Input.Search
                            placeholder="搜索用户名或邮箱"
                            allowClear
                            onSearch={value => setSearchText(value)}
                            style={{ width: 250 }}
                        />
                        <Button
                            type="primary"
                            icon={<UserAddOutlined />}
                            onClick={showCreateModal}
                        >
                            添加用户
                        </Button>
                    </Space>
                }
                tabList={[
                    {
                        key: 'active',
                        tab: <span><CheckCircleOutlined /> 活跃用户</span>,
                    },
                    {
                        key: 'pending',
                        tab: <span><ClockCircleOutlined /> 待审核用户{stats.pendingUsers > 0 && <Badge count={stats.pendingUsers} style={{ marginLeft: 8 }} />}</span>,
                    },
                ]}
                activeTabKey={activeTabKey}
                onTabChange={key => setActiveTabKey(key)}
            >
                {activeTabKey === 'active' ? (
                    <Table
                        columns={activeColumns}
                        dataSource={users}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            showSizeChanger: true,
                            showTotal: total => `共 ${total} 个用户`,
                        }}
                    />
                ) : (
                    <Table
                        columns={pendingColumns}
                        dataSource={pendingUsers}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            showSizeChanger: true,
                            showTotal: total => `共 ${total} 个待审核用户`,
                        }}
                        locale={{ emptyText: '没有待审核的用户' }}
                    />
                )}
            </Card>

            <Modal
                title={editingUser ? `编辑用户: ${editingUser.username}` : "创建新用户"}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={() => setIsModalVisible(false)}
                confirmLoading={loading}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        role: 'user',
                        status: 'active',
                        phone_country_code: '+86'
                    }}
                >
                    <Form.Item
                        name="username"
                        label="用户名"
                        rules={[
                            { required: true, message: '请输入用户名!' },
                            { min: 3, message: '用户名至少3个字符!' }
                        ]}
                    >
                        <Input placeholder="用户名" disabled={!!editingUser} />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label="电子邮箱"
                        rules={[
                            { required: true, message: '请输入电子邮箱!' },
                            { type: 'email', message: '请输入有效的电子邮箱地址!' }
                        ]}
                    >
                        <Input placeholder="user@example.com" />
                    </Form.Item>

                    {!editingUser && (
                        <Form.Item
                            name="password"
                            label="密码"
                            rules={[
                                { required: true, message: '请输入密码!' },
                                { min: 6, message: '密码至少6个字符!' }
                            ]}
                            tooltip="留空将自动生成随机密码"
                        >
                            <Input.Password placeholder="留空将自动生成随机密码" />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="role"
                        label="角色"
                        rules={[{ required: true, message: '请选择角色!' }]}
                    >
                        <Select>
                            <Option value="user">普通用户</Option>
                            <Option value="admin">管理员</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="status"
                        label="状态"
                        rules={[{ required: true, message: '请选择状态!' }]}
                    >
                        <Select>
                            <Option value="active">活跃</Option>
                            <Option value="inactive">未激活</Option>
                            <Option value="locked">锁定</Option>
                            <Option value="pending">待审核</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item label="电话">
                        <Input.Group compact>
                            <Form.Item name="phone_country_code" noStyle>
                                <Select
                                    style={{ width: 140 }}
                                    showSearch
                                    filterOption={(input, option) =>
                                        String(option.search || '').toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {phoneCountryCodes.map(item => (
                                        <Option key={item.code} value={item.code} search={`${item.code} ${item.short} ${item.label}`}>
                                            <CountryFlag code={item.short} />{item.code} {item.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item name="phone" noStyle>
                                <Input style={{ width: 'calc(100% - 140px)' }} placeholder="电话号码" />
                            </Form.Item>
                        </Input.Group>
                    </Form.Item>

                    <Form.Item
                        name="company"
                        label="公司"
                    >
                        <Input placeholder="公司名称" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 余额管理模态框 */}
            <Modal
                title={`余额管理 - ${selectedUserForBalance?.username || ''}`}
                open={balanceModalVisible}
                onCancel={() => setBalanceModalVisible(false)}
                onOk={handleAdjustBalance}
                confirmLoading={loading}
                okText="确定"
                cancelText="取消"
            >
                <Form
                    form={balanceForm}
                    layout="vertical"
                    initialValues={{
                        description: ''
                    }}
                >
                    <Alert
                        message={
                            <div>
                                当前余额: <Tag color="green">{formatMoney(parseFloat(selectedUserForBalance?.balance) || 0, selectedUserForBalance?.balance_currency || PLATFORM_CURRENCY)}</Tag>
                            </div>
                        }
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />

                    <Form.Item
                        name="amount"
                        label="调整金额"
                        rules={[
                            { required: true, message: '请输入调整金额' },
                            {
                                validator: (_, value) => {
                                    if (!value) return Promise.resolve();
                                    const num = parseFloat(value);
                                    if (isNaN(num)) {
                                        return Promise.reject(new Error('请输入有效的数字'));
                                    }
                                    if (num === 0) {
                                        return Promise.reject(new Error('金额不能为0'));
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                        extra="正数为充值，负数为扣款"
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            placeholder="输入金额，正数充值，负数扣款"
                            precision={2}
                            step={10}
                        />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="说明"
                        rules={[{ required: true, message: '请输入调整说明' }]}
                    >
                        <Input.TextArea
                            rows={3}
                            placeholder="请输入调整原因或备注"
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <UserDetailDrawer
                visible={detailDrawerVisible}
                user={selectedUserForDetail}
                onClose={() => setDetailDrawerVisible(false)}
                onChanged={fetchUsers}
            />
        </div>
    );
};

export default UserManagement;
